import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/microsoft-ads")({
  head: () => {
    const h = pageHead("pmMicrosoftAds", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Microsoft Ads Agency",
          description: "Bing Search, LinkedIn profile targeting, Audience Network — managed as complement in the search mix, not as isolated island.",
          url: "/en/services/performance-marketing/microsoft-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "Microsoft Ads", path: "/en/services/performance-marketing/microsoft-ads" },
        ])),
      ],
    };
  },
  component: MicrosoftAdsEN,
});

const steps = [
  { num: "01", title: "Analysis & Google Mix Check (3–5 days)", body: "Account audit (if Microsoft activity is already running), analysis of existing Google setup as complementary base, audience affinity check, competitive analysis in Bing search results. Output: concrete hypotheses plus economic assessment." },
  { num: "02", title: "Setup with Microsoft Specifics (1–2 weeks)", body: "UET tag plus Conversions API integration. Account structure Microsoft-specific — not from Google import, but freshly conceived. LinkedIn profile targeting where appropriate. Audience Network selectively. First bid strategy adjustments." },
  { num: "03", title: "Iteration & Search Mix Optimization (from week 2–3)", body: "Iteration on audience sharpness, match-type discipline, negative-list maintenance. Parallel management with Google Ads as complementary layer — we shift budgets dynamically between Google and Microsoft." },
  { num: "04", title: "Scaling & Ongoing Operations (continuous)", body: "Budget scaling in working areas. LinkedIn profile targeting expansion in B2B context. Monthly performance review in integrated search context. Operational management typically directly by the managing director." },
];

const capabilities = [
  { name: "Bing Search Campaigns", detail: "Brand · non-brand · long-tail" },
  { name: "LinkedIn Profile Targeting", detail: "Job Function · Company · Industry" },
  { name: "Microsoft Shopping", detail: "Feed hygiene via Delta" },
  { name: "Audience Network selectively", detail: "MSN · Outlook · Edge" },
  { name: "UET Tag & Conversions API", detail: "Server-side · match quality" },
  { name: "Smart Bidding", detail: "Learning-phase discipline" },
  { name: "Negative-list hygiene", detail: "Rule-based · automated" },
  { name: "Search Mix Management", detail: "Google + Microsoft integrated" },
];

const audience = [
  { tag: "Primary", title: "B2B Lead Generation", body: "Microsoft Ads works particularly well in B2B because the Bing audience has higher affinity to professional use. LinkedIn profile targeting in search context is the biggest lever — available only at Microsoft, not at Google." },
  { tag: "Secondary", title: "B2C E-commerce as Search Complement", body: "Microsoft Ads as a complement to Google Search is worthwhile for e-commerce setups from monthly budgets of around €5,000 in Google. Lower CPCs plus more affluent audience often lead to better ROAS values." },
  { tag: "Tertiary", title: "SaaS with B2B Focus", body: "Works for SaaS setups where the buyer audience is strongly represented in the Microsoft ecosystem (Office, Azure, LinkedIn). Bottom-funnel search plus LinkedIn profile targeting as account-based layer." },
  { tag: "Disqualification", title: "When we're not the right partner", body: "For isolated Microsoft Ads mandates under €1,500 monthly budget. When the expectation is “double my search traffic” — Microsoft Ads isn't a volume channel. When Google Ads isn't cleanly set up yet." },
];

const realistic = [
  "CPC advantage of 30–50% over Google is typical in the DACH market — lower auction pressure at same or similar audience quality.",
  "ROAS advantage of 15–30% over Google in B2B setups is a typical range when LinkedIn profile targeting is systematically used.",
  "Search mix diversification of 10–20% volume leverage — Microsoft Ads complements Google rather than replacing it. Volume contribution is relatively small but additional.",
  "Conversion stability in B2B audiences higher than Google — hard to quantify in percent but observable in most B2B setups.",
];

const value = [
  ["Search mix diversification instead of Google monoculture.", "Risk spreading across two search channels, not dependency on one."],
  ["LinkedIn profile targeting in search context.", "Exclusive lever for B2B, not available at Google."],
  ["Lower CPCs at comparable conversion quality.", "Efficiency lever in the entire search setup."],
  ["Microsoft-native account logic instead of Google import.", "We build channel-specific, not copy-paste."],
  ["Operational management by experienced hand.", "Directly by the managing director in most mandates."],
  ["Compensation model without budget coupling.", "We don't earn more when you spend more."],
];

const related = [
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "Complementary search channel with higher volume share" },
  { to: "/en/services/performance-marketing/linkedin-ads", name: "LinkedIn Ads", sub: "B2B specialization with own audience logic" },
  { to: "/en/services/tracking-data-foundation", name: "Tracking & Data Foundation", sub: "Clean data as prerequisite for search mix management" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, PMax and Demand Gen — performance on Google's owned surfaces." },
  { to: "/en/services/performance-marketing/linkedin-ads", name: "LinkedIn Ads", sub: "B2B targeting by function, industry and company size." },
];

function MicrosoftAdsEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Microsoft Ads"
          headline={<>Microsoft Ads Agency — <em>Search Complement for DACH,</em> Not a Google Clone.</>}
          lead="Bing Search, LinkedIn profile targeting, Audience Network — managed as a complement in the search mix, not as an isolated island. Calvarius works as a Microsoft Ads agency for B2B setups, SaaS providers, and B2C e-commerce wanting to economically diversify their search mix."
          metaStrip={["B2B Affinity", "Low CPCs", "LinkedIn Profile Targeting"]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "Microsoft Ads" },
          ]}
        />

        <Section number="02" kicker="Position" headline={<>What a Microsoft Ads Agency in the DACH market <em>really delivers.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for a Microsoft Ads agency for the German-speaking market typically has a concrete question: is the effort worthwhile for a channel with only about 7-10% market share in the DACH search market? The honest answer: yes in many setups, no in some. Microsoft Ads isn't a volume channel like Google — it's an <strong className="font-semibold text-ink">efficiency and diversification channel</strong>. Anyone wanting to spread search risk, address a more affluent audience, or use LinkedIn profile targeting in search context, finds real economic leverage in Microsoft Ads — if the setup is economically large enough that operational maintenance pays off.</p>
              <p className="mb-5">Calvarius doesn't treat Microsoft Ads as a Google copy. That would be the most common mistake — importing Google Ads campaigns 1:1 into Microsoft and hoping it works. In fact, Microsoft Ads has its own strengths: significantly lower CPCs (often 30–50% below Google), higher conversion stability in B2B audiences, unique LinkedIn profile targeting options (Job Function, Company, Industry) directly in search context.</p>
              <p>Concretely: we build Microsoft-specific account structures. UET tag plus server-side tracking for clean attribution. Audience Network selectively where economically viable. And we connect Microsoft Ads with the existing <Link to="/en/services/performance-marketing/google-ads" className="underline">Google Ads strategy</Link> as a complementary layer, not a competitive construct.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience Fit" headline={<>When you need a Microsoft Ads Agency <em>— and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our Microsoft Ads Agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic performance agencies often treat Microsoft Ads as a Google import job and need three to six weeks for a setup without real Microsoft specifics, we are productive within two weeks with a channel-native setup.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our Microsoft Ads Agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>Microsoft Ads management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Platform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Microsoft Advertising UI</li>
                  <li>Advertising Editor (bulk)</li>
                  <li>UET tag + Conversions API</li>
                  <li>Microsoft Merchant Center</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side via GTM Server</li>
                  <li>Google Analytics 4</li>
                  <li>Looker Studio search-mix dashboards</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/en/services/delta" className="font-medium text-ink hover:text-cyan-deep">Delta →</Link><br/><span className="text-ink-3">Feed management also for Microsoft Shopping</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of <em>Microsoft Ads optimization.</em></>}
          caption="Microsoft Ads isn't a scaling channel but a diversification and efficiency channel.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>Microsoft Ads Agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Get concrete">
          <CTABlock
            headline={<>Microsoft Ads audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation, we clarify whether Microsoft Ads makes economic sense in your setup — as complement to Google or as B2B specialist channel. You get an honest assessment."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
